import { atom } from "nanostores";

import { useStore } from "@nanostores/react";

import { isDevOverlayEnvironment } from "./featureFlagOverrides";
import { type UiCapabilities, type UiCapabilityKey } from "./uiCapabilities";

const STORAGE_KEY = "vega:capability-overrides";

type CapabilityOverrides = Partial<Record<UiCapabilityKey, boolean>>;

function loadFromStorage(): CapabilityOverrides {
  if (!isDevOverlayEnvironment()) return {};

  try {
    const raw = localStorage.getItem(STORAGE_KEY);

    return raw === null ? {} : (JSON.parse(raw) as CapabilityOverrides);
  } catch {
    return {};
  }
}

function saveToStorage(overrides: CapabilityOverrides): void {
  if (!isDevOverlayEnvironment()) return;

  if (Object.keys(overrides).length === 0) {
    localStorage.removeItem(STORAGE_KEY);

    return;
  }

  localStorage.setItem(STORAGE_KEY, JSON.stringify(overrides));
}

export const $uiCapabilityOverrides =
  atom<CapabilityOverrides>(loadFromStorage());

export function setCapabilityOverride(
  key: UiCapabilityKey,
  value: boolean,
): void {
  if (!isDevOverlayEnvironment()) return;

  const next = { ...$uiCapabilityOverrides.get(), [key]: value };
  $uiCapabilityOverrides.set(next);
  saveToStorage(next);
}

export function clearCapabilityOverride(key: UiCapabilityKey): void {
  if (!isDevOverlayEnvironment()) return;

  const current = $uiCapabilityOverrides.get();
  const next = Object.fromEntries(
    Object.entries(current).filter(([overrideKey]) => overrideKey !== key),
  ) as CapabilityOverrides;
  $uiCapabilityOverrides.set(next);
  saveToStorage(next);
}

export function clearAllCapabilityOverrides(): void {
  if (!isDevOverlayEnvironment()) return;

  $uiCapabilityOverrides.set({});
  saveToStorage({});
}

export function setAllCapabilityOverrides(
  keys: readonly UiCapabilityKey[],
  value: boolean,
): void {
  if (!isDevOverlayEnvironment()) return;

  const next = Object.fromEntries(
    keys.map((key) => [key, value]),
  ) as CapabilityOverrides;
  $uiCapabilityOverrides.set(next);
  saveToStorage(next);
}

export function applyUiCapabilityOverrides(
  capabilities: UiCapabilities,
  overrides: CapabilityOverrides,
): UiCapabilities {
  if (!isDevOverlayEnvironment()) return capabilities;

  return { ...capabilities, ...overrides };
}

export function useUiCapabilityOverridesKey(): string {
  const overrides = useStore($uiCapabilityOverrides);

  return JSON.stringify(overrides);
}
